import { api } from "@/lib/api";

export interface KorisnikZaDodjelu {
  id: string;
  full_name: string;
  email: string;
}

export async function dohvatiKorisnikePoUlozi(
  uloga: string,
  signal?: AbortSignal,
): Promise<KorisnikZaDodjelu[]> {
  const response = await api.get<KorisnikZaDodjelu[]>("/korisnici", {
    params: { uloga },
    signal,
  });
  return response.data;
}

// --- Administration -----------------------------------------------------------

const UPRAVLJANJE = "/korisnici/upravljanje";

export interface Uloga {
  code: string;
  name_hr: string;
  description: string | null;
}

export interface Korisnik {
  id: string;
  email: string;
  full_name: string;
  telefon: string | null;
  is_active: boolean;
  must_change_password: boolean;
  mfa_required: boolean;
  mfa_confirmed: boolean;
  uloge: Uloga[];
  radnik: { id: string; ime: string; prezime: string } | null;
  zadnja_prijava: string | null;
  /** Set together, and only on an archived account. */
  archived_at: string | null;
  archived_email: string | null;
}

/** What the account has left behind. Deleting is offered only at zero. */
export interface Reference {
  ukupno: number;
  po_tablici: Record<string, number>;
}

export interface RadnikZaPovezivanje {
  id: string;
  ime: string;
  prezime: string;
  korisnik_id: string | null;
}

export interface NoviKorisnik {
  email: string;
  full_name: string;
  telefon?: string | null;
  uloge: string[];
  radnik_id: string | null;
}

export type IzmjenaKorisnika = Omit<NoviKorisnik, "email"> & { email?: string };

/** Shown once: the server keeps only the hash. */
export interface PrivremenaLozinka {
  korisnik: Korisnik;
  privremena_lozinka: string;
}

export async function dohvatiKorisnike(
  ukljuciArhivirane = false,
): Promise<Korisnik[]> {
  return (
    await api.get<Korisnik[]>(UPRAVLJANJE, {
      params: ukljuciArhivirane ? { include_archived: true } : undefined,
    })
  ).data;
}

export async function dohvatiReference(id: string): Promise<Reference> {
  return (await api.get<Reference>(`${UPRAVLJANJE}/${id}/reference`)).data;
}

export async function obrisiKorisnika(id: string): Promise<void> {
  await api.delete(`${UPRAVLJANJE}/${id}`);
}

export async function arhivirajKorisnika(id: string): Promise<Korisnik> {
  return (await api.post<Korisnik>(`${UPRAVLJANJE}/${id}/arhiviranje`)).data;
}

export async function dohvatiUloge(): Promise<Uloga[]> {
  return (await api.get<Uloga[]>(`${UPRAVLJANJE}/uloge`)).data;
}

export async function dohvatiRadnikeZaPovezivanje(): Promise<
  RadnikZaPovezivanje[]
> {
  return (await api.get<RadnikZaPovezivanje[]>(`${UPRAVLJANJE}/radnici`)).data;
}

export async function stvoriKorisnika(
  podaci: NoviKorisnik,
): Promise<PrivremenaLozinka> {
  return (await api.post<PrivremenaLozinka>(UPRAVLJANJE, podaci)).data;
}

export async function izmijeniKorisnika(
  id: string,
  podaci: IzmjenaKorisnika,
): Promise<Korisnik> {
  return (await api.patch<Korisnik>(`${UPRAVLJANJE}/${id}`, podaci)).data;
}

export async function postaviAktivnost(
  id: string,
  aktivan: boolean,
): Promise<Korisnik> {
  const radnja = aktivan ? "aktivacija" : "deaktivacija";
  return (await api.post<Korisnik>(`${UPRAVLJANJE}/${id}/${radnja}`)).data;
}

export async function resetirajLozinku(id: string): Promise<PrivremenaLozinka> {
  return (
    await api.post<PrivremenaLozinka>(`${UPRAVLJANJE}/${id}/reset-lozinke`)
  ).data;
}

export async function updateMfaPolicy(
  id: string, mfaRequired: boolean, password: string,
): Promise<Korisnik> {
  return (await api.patch<Korisnik>(`${UPRAVLJANJE}/${id}/mfa-policy`, {
    mfa_required: mfaRequired, password,
  })).data;
}

export async function resetUserMfa(
  id: string, disableRequirement: boolean, password: string,
): Promise<Korisnik> {
  return (await api.post<Korisnik>(`${UPRAVLJANJE}/${id}/mfa-reset`, {
    disable_requirement: disableRequirement, password,
  })).data;
}
